'use client';

import { useState, useEffect, useRef } from 'react'
import { 
  Facebook, Mail, Phone, MessageCircle, 
  MapPin, Clock, ArrowUp, Heart, Globe, 
  Send, Linkedin, Twitter, Instagram 
} from 'lucide-react'
import { translations } from '@/lib/translations'
import logo from '../lib/asset/adb9cab8-9aa6-4251-9cd8-18bf93f66b63_removalai_preview.png'

export default function Footer({ language }) {
  const t = translations[language]
  const currentYear = new Date().getFullYear()
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [email, setEmail] = useState('')
  const [isVisible, setIsVisible] = useState(false)
  const footerRef = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.unobserve(entry.target)
        }
      },
      { threshold: 0.1 }
    )

    if (footerRef.current) {
      observer.observe(footerRef.current)
    }

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleNewsletterSubmit = (e) => {
    e.preventDefault()
    // Handle newsletter signup
    console.log('Newsletter signup:', email)
    setEmail('')
    // Show success message (you can add toast notification here)
  }

  const quickLinks = [
    { id: 'home', label: t.nav.home, icon: '🏠' },
    { id: 'about', label: t.nav.about, icon: '📌' },
    { id: 'services', label: t.nav.services, icon: '⚙️' },
    { id: 'portfolio', label: t.nav.portfolio, icon: '🖼️' },
    { id: 'testimonials', label: t.nav.testimonials, icon: '💬' },
    { id: 'contact', label: t.nav.contact, icon: '📞' },
  ]

  const socialLinks = [
    { icon: Facebook, href: 'https://www.facebook.com/profile.php?id=61586128531565', label: 'Facebook', color: 'hover:bg-blue-600' },
    { icon: MessageCircle, href: 'https://t.me/Naol06', label: 'Telegram', color: 'hover:bg-sky-500' },
    { icon: Twitter, href: '#', label: 'Twitter', color: 'hover:bg-blue-400' },
    { icon: Instagram, href: '#', label: 'Instagram', color: 'hover:bg-pink-600' },
    { icon: Linkedin, href: '#', label: 'LinkedIn', color: 'hover:bg-blue-700' },
  ]

  const contactInfo = [
    { icon: Mail, href: 'mailto:fortinotech@gmail.com', text: 'fortinotech@gmail.com', label: 'Email' },
    { icon: Phone, href: 'tel:+251979235206', text: '+251 979 235 206', label: 'Phone' },
    { icon: MapPin, text: t.contact?.address || 'Addis Ababa, Jemo Michael, Peony Commercial Building, Office No. 306/A', label: 'Location' },
  ]

  return (
    <>
      <footer 
        ref={footerRef}
        className="relative bg-gradient-to-b from-background to-card border-t border-border/50 overflow-hidden"
      >
        {/* Premium Background Effects */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Animated gradient orbs */}
          <div className="absolute top-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl animate-pulse animation-delay-2000" />
          
          {/* Grid pattern */}
          <div className="absolute inset-0 opacity-[0.02]" style={{
            backgroundImage: `
              radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0),
              linear-gradient(45deg, transparent 48%, currentColor 49%, currentColor 51%, transparent 52%)
            `,
            backgroundSize: '50px 50px, 30px 30px'
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          {/* Main Footer Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 mb-12">
            {/* Company Info with Animation */}
            <div className={`transform transition-all duration-700 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
              <div className="flex items-center gap-3 mb-6 group/logo">
                <div className="relative">
                  <div className="absolute -inset-2 bg-gradient-to-r from-primary to-secondary rounded-full blur-lg opacity-30 group-hover/logo:opacity-60 transition-opacity" />
                  <img 
                    src={logo} 
                    alt="Fortino Tech" 
                    className="relative w-14 h-14 rounded-full border-2 border-primary/30 group-hover/logo:border-primary/60 transition-all duration-300 transform group-hover/logo:scale-105" 
                  />
                </div>
                <div>
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                    Fortino Tech
                  </h3>
                  <p className="text-xs text-muted-foreground">Innovation & Excellence</p>
                </div>
              </div>
              
              <p className="text-muted-foreground leading-relaxed mb-6">
                {t.footer.description}
              </p>

              {/* Newsletter Signup */}
              <form onSubmit={handleNewsletterSubmit} className="relative group/input">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email for updates"
                  className="w-full px-4 py-3 pr-12 rounded-xl bg-background border border-border/50 focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all text-sm"
                  required
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg bg-primary/10 hover:bg-primary/20 transition-all group/btn"
                >
                  <Send className="w-4 h-4 text-primary group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
                </button>
              </form>
            </div>

            {/* Quick Links with Animation */}
            <div className={`transform transition-all duration-700 delay-200 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
              <h4 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
                <span className="w-1 h-5 bg-primary rounded-full" />
                {t.footer.quickLinks}
              </h4>
              
              <ul className="space-y-3">
                {quickLinks.map((link, index) => (
                  <li key={link.id}>
                    <a
                      href={`#${link.id}`}
                      className="group/link flex items-center gap-3 text-muted-foreground hover:text-primary transition-all duration-300"
                    >
                     
                      <span className="text-sm group-hover/link:translate-x-1 transition-transform">
                        {link.label}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info with Animation */}
            <div className={`transform transition-all duration-700 delay-400 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
              <h4 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
                <span className="w-1 h-5 bg-secondary rounded-full" />
                {t.footer.contact}
              </h4>
              
              <div className="space-y-4">
                {contactInfo.map((item, index) => {
                  const Icon = item.icon
                  return (
                    <div key={index} className="group/contact">
                      {item.href ? (
                        <a
                          href={item.href}
                          className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-all duration-300"
                        >
                          <div className="p-2 rounded-lg bg-primary/5 group-hover/contact:bg-primary/10 transition-colors">
                            <Icon className="w-4 h-4" />
                          </div>
                          <span className="text-sm">{item.text}</span>
                        </a>
                      ) : (
                        <div className="flex items-center gap-3 text-muted-foreground">
                          <div className="p-2 rounded-lg bg-primary/5">
                            <Icon className="w-4 h-4" />
                          </div>
                          <span className="text-sm">{item.text}</span>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Social & Additional Info */}
            <div className={`transform transition-all duration-700 delay-600 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
              <h4 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
                <span className="w-1 h-5 bg-accent rounded-full" />
                Connect With Us
              </h4>

              {/* Social Media Icons */}
              <div className="flex flex-wrap gap-3 ">
                {socialLinks.map((social, index) => {
                  const Icon = social.icon
                  return (
                    <a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`
                        p-3 rounded-xl bg-muted/50 hover:bg-primary/10
                        border border-border/50 hover:border-primary/30
                        transition-all duration-300 transform hover:scale-110
                        group/social
                      `}
                      aria-label={social.label}
                    >
                      <Icon className="w-4 h-4 text-muted-foreground group-hover/social:text-primary transition-colors" />
                    </a>
                  )
                })}
              </div>

            
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="relative border-t border-border/50">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              {/* Copyright */}
              <p className="text-sm text-muted-foreground text-center sm:text-left">
                © {currentYear} Fortino Tech. {t.footer.allRights}
              </p>

             

              {/* Language & Legal Links */}
              <div className="flex items-center gap-4">
                <button className="text-xs text-muted-foreground hover:text-primary transition-colors">
                  Privacy Policy
                </button>
                <button className="text-xs text-muted-foreground hover:text-primary transition-colors">
                  Terms of Service
                </button>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Globe className="w-3 h-3" />
                  <span>{language === 'en' ? 'English' : 'አማርኛ'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className={`
          fixed bottom-6 right-6 z-40
          p-3 rounded-full
          bg-gradient-to-r from-primary to-secondary
          text-white shadow-lg
          transform transition-all duration-500
          hover:scale-110 hover:shadow-xl
          group/scroll
          ${showScrollTop ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}
        `}
        aria-label="Scroll to top"
      >
        <ArrowUp className="w-5 h-5 group-hover/scroll:-translate-y-1 transition-transform" />
      </button>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-5px); }
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        /* Responsive adjustments */
        @media (max-width: 640px) {
          .grid {
            gap: 2rem;
          }
        }
      `}</style>
    </>
  )
}